import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CHAPTERS, CHAPTER_COUNT, TOTAL_SERVICES } from './storyData';
import { INDUSTRY_ICONS } from '../../data/industriesData';
import { ServiceIcon } from '../../data/serviceIcons';

// ─── MOBILE STORY ─────────────────────────────────────────────────────────────
// Same narrative, told the way a phone can actually tell it: the sector header
// sticks while you scroll its own services, and whichever service is nearest
// the middle of the screen lights up while the rest recede.
//
// No GSAP, no scroll maths, no extra scroll budget - one IntersectionObserver
// with a mid-viewport band does the whole thing. A phone should not be asked to
// scroll through ten screens of choreography to read a service list.
//
// Browsers with scroll-driven CSS animations get the same effect natively; this
// is the floor, not a downgrade.

export default function MobileStory() {
  return (
    <section className="zx-story zx-mobile" aria-label="Industries and the services we deliver in each">
      <header className="zx-head">
        <span className="zx-head__eyebrow">Our Services</span>
        <h2 className="zx-head__title">
          End-to-End Engineering, <span>Everything</span> Under One Roof
        </h2>
        <p className="zx-head__sub">
          {CHAPTER_COUNT} industries. {TOTAL_SERVICES} purpose-built services.
        </p>
      </header>

      {CHAPTERS.map(chapter => <MobileChapter key={chapter.id} chapter={chapter} />)}
    </section>
  );
}

function MobileChapter({ chapter }) {
  const listRef = useRef(null);
  const [lit, setLit] = useState(0);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const items = Array.from(list.querySelectorAll('[data-mitem]'));
    if (!items.length) return undefined;

    // A narrow band across the middle of the viewport. Whatever sits in it is
    // the service being read, so the highlight tracks reading position rather
    // than merely "has entered the screen".
    const observer = new IntersectionObserver(
      entries => {
        let best = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
        }
        if (best) setLit(Number(best.target.dataset.index));
      },
      { rootMargin: '-42% 0px -42% 0px', threshold: [0, 0.5, 1] }
    );

    items.forEach(item => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  const Glyph = INDUSTRY_ICONS[chapter.id];

  return (
    <div className="zx-mchapter" style={{ color: chapter.accent }}>
      <div className="zx-mchapter__sticky">
        <span
          className="zx-mchapter__glyph"
          style={{
            background: `${chapter.accent}16`,
            border: `1.5px solid ${chapter.accent}38`,
          }}
        >
          {chapter.img
            ? <img src={chapter.img} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
            : Glyph ? <Glyph c={chapter.accent} /> : null}
        </span>
        <span>
          <span className="zx-mchapter__id">
            Sector {chapter.id} / {String(CHAPTER_COUNT).padStart(2, '0')}
          </span>
          <h3 className="zx-mchapter__name">{chapter.name}</h3>
        </span>
      </div>

      <p className="zx-mchapter__desc">{chapter.desc}</p>

      <ul className="zx-mlist" ref={listRef}>
        {chapter.services.map((service, i) => (
          <li
            key={service.slug}
            className="zx-mitem"
            data-mitem
            data-index={i}
            data-lit={i === lit ? 'true' : 'false'}
          >
            <Link className="zx-mitem__link" to={service.to} style={{ borderColor: `${chapter.accent}2E` }}>
              <ServiceIcon slug={service.slug} color={chapter.accent} size={22} />
              <span>
                <span className="zx-mitem__title">{service.title}</span>
                <span className="zx-mitem__brief">{service.brief}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <Link className="zx-mchapter__cta" to={chapter.to} style={{ color: chapter.accent }}>
        Explore Industry
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M2 5h6M5.5 2.5L8 5l-2.5 2.5" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </div>
  );
}
